// src/public/components/FormRenderer.jsx

/**
 * FormRenderer Component and related utilities
 * Handles the rendering and interaction of dynamic form content with multilingual support
 */

import { motion } from "framer-motion";
import { Plus, RefreshCw, Trash2, Type } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { AreaCodeMaxLength } from "../../constants/common.js";
import { Priority, t, TRANSLATIONS } from "../../constants/locale";
import { CAPTCHA_URL, SUBMISSION_URL } from "../../constants/urls.js";
import { PREFIX } from "../../router";
import { formatDateTime, parseHKTDatetimeStr } from "../../utils/datetimeUtil";
import FontSizeGuide from "./FontSizeGuide.jsx";

const splitAreaCodeAndNumber = (value) => {
  let areaCode = "";
  let phoneNumber = "";
  if (value.includes("-")) {
    [areaCode, phoneNumber] = value.split("-");
  } else {
    phoneNumber = value;
  }
  return [areaCode, phoneNumber];
};

// In FormRenderer, we should initialize formData based on question types:
const initializeFormData = (questions) => {
  console.log("initializeFormData");
  const initialData = {};
  questions.forEach((question) => {
    switch (question.type) {
      case "list_text":
        // Initialize main array for group
        initialData[question.field_name] = [""];
        // Initialize first item with underscore and 0-based index
        initialData[`${question.field_name}_0`] = ""; // Start with index 0
        break;

      case "list_subform":
        // Recursively initialize subform data
        initialData[question.field_name] = [
          initializeFormData(question.questions),
        ];
        break;

      case "checkboxgroup":
        // Initialize main array for group
        initialData[question.field_name] = [];
        // Initialize individual checkboxes and their other inputs
        question.options?.forEach((option, index) => {
          initialData[`${question.field_name}_${index}`] = false;
          if (option.other_option) {
            initialData[`${question.field_name}_${index}_other`] = "";
          }
        });
        break;

      case "checkbox":
        initialData[question.field_name] = false;
        break;

      case "radio":
      case "dropdown":
        initialData[question.field_name] = "";
        break;

      case "text":
      case "textarea":
      case "time":
      case "date":
        initialData[question.field_name] = "";
        break;

      case "label":
        break;

      default:
        initialData[question.field_name] = "";
        break;
    }
  });

  console.log(initialData);
  return initialData;
};

const getDominatingOption = (questions, formData, mutexId) => {
  // Sort questions by index to respect precedence
  const mutexQuestions = questions
    .filter((q) => q.mutex_id === mutexId)
    .sort((a, b) => questions.indexOf(a) - questions.indexOf(b));

  for (const question of mutexQuestions) {
    const selectedValue = formData[question.field_name];
    if (
      selectedValue !== null &&
      selectedValue !== undefined &&
      selectedValue !== ""
    ) {
      const selectedOption = question.options[selectedValue];
      if (selectedOption?.exclude_mutex && selectedOption?.dominating_option) {
        return {
          question,
          option: selectedOption,
          value: selectedValue,
        };
      }
    }
  }
  return null;
};

// For debug purpose
const FormDataPanel = ({ formData, validationErrors }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className="fixed bottom-0 right-0 w-96 bg-white border-l border-t shadow-lg z-50">
      {/* Header */}
      <div
        className="flex items-center justify-between p-2 bg-gray-100 cursor-pointer select-none"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3 className="font-medium text-gray-700">Form Data Inspector</h3>
        <button className="text-gray-500 hover:text-gray-700">
          {isExpanded ? "▼" : "▲"}
        </button>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 h-96 overflow-y-auto">
          {/* Form Data Section */}
          <div className="mb-4">
            <h4 className="font-medium text-gray-700 mb-2">Form Data:</h4>
            <pre className="bg-gray-50 p-2 rounded text-sm overflow-x-auto">
              {JSON.stringify(formData, null, 2)}
            </pre>
          </div>

          {/* Validation Errors Section */}
          <div>
            <h4 className="font-medium text-gray-700 mb-2">
              Validation Errors:
            </h4>
            <pre className="bg-gray-50 p-2 rounded text-sm overflow-x-auto">
              {JSON.stringify(validationErrors, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Language Switcher Component
 * Provides UI for switching between supported languages
 */
const LangSwitch = ({ currentLang, onLanguageChange, supportedLanguages }) => {
  if (!supportedLanguages || supportedLanguages.length <= 1) return null;

  const otherLanguages = Priority.filter(
    (lang) => lang !== currentLang && supportedLanguages.includes(lang)
  );

  return (
    <div className="flex -space-x-px">
      {" "}
      {/* Negative space to join buttons */}
      {otherLanguages.map((lang, index) => (
        <button
          key={lang}
          onClick={() => onLanguageChange(lang)}
          className={`
            min-w-[32px] h-8 px-2
            text-blue-600 bg-green-0 hover:bg-green-100
            text-sm
            ${index === 0 ? "rounded-l border-r border-gray-200" : "rounded-r"}
          `}
        >
          {TRANSLATIONS[lang].locale_symbol}
        </button>
      ))}
    </div>
  );
};

const FontSizeSwitch = ({ fontSize, onFontSizeChange, language }) => {
  const sizes = [
    {
      key: "S",
      value: "1",
      label: {
        en: "S",
        tc: "小",
        sc: "小",
      },
    },
    {
      key: "M",
      value: "1.125",
      label: {
        en: "M",
        tc: "中",
        sc: "中",
      },
    },
    {
      key: "L",
      value: "1.25",
      label: {
        en: "L",
        tc: "大",
        sc: "大",
      },
    },
  ];

  const currentSize = sizes.find((s) => s.value === fontSize) || sizes[0];
  const nextSize =
    sizes[(sizes.findIndex((s) => s.value === fontSize) + 1) % sizes.length];

  return (
    <div className="flex justify-end mb-1">
      <button
        onClick={() => onFontSizeChange(nextSize.value)}
        className="w-14 h-9 text-blue-600 bg-green-0 rounded text-sm hover:bg-green-100"
      >
        <Type
          size={14}
          className="inline-block mr-1"
          style={{ marginTop: -5 }}
        />
        {nextSize.label[language]}
      </button>
    </div>
  );
};

/**
 * Logo Component
 * Displays form logo with title, handling both base64 and URL sources
 */
// LogoComponent
const LogoComponent = ({ logo_base64, logo_url, title, language }) => {
  const logoSrc = logo_url?.[language] || logo_base64?.[language];
  const titleText = title[language] || "";

  const [logoHeight, setLogoHeight] = React.useState(0);
  const [titleWidth, setTitleWidth] = React.useState(0);
  const [containerWidth, setContainerWidth] = React.useState(0);

  const containerRef = React.useRef(null);
  const titleRef = React.useRef(null);

  // Get original logo aspect ratio for height calculation
  React.useEffect(() => {
    if (logoSrc) {
      const img = new Image();
      img.onload = () => {
        setLogoHeight((240 * img.naturalHeight) / img.naturalWidth);
      };
      img.src = logoSrc;
    }
  }, [logoSrc]);

  // Measure container and title width
  React.useEffect(() => {
    const updateMeasurements = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
      if (titleRef.current) {
        setTitleWidth(titleRef.current.offsetWidth);
      }
    };

    updateMeasurements();
    window.addEventListener("resize", updateMeasurements);
    return () => window.removeEventListener("resize", updateMeasurements);
  }, [titleText]);

  // Calculate if title needs to wrap (for spacing from logo)
  const maxLogoWidth = 240;
  const horizontalSpacing = 48;
  const shouldWrap =
    maxLogoWidth + titleWidth + horizontalSpacing > containerWidth;

  return (
    <div ref={containerRef} className="mb-4">
      <div className="relative px-6 py-2">
        {/* Logo - Always top left */}
        {logoSrc && (
          <div
            style={{
              width: `${maxLogoWidth}px`,
              height: `${logoHeight}px`,
            }}
            className="flex items-center"
          >
            <img
              src={logoSrc}
              alt={titleText}
              className="w-full object-contain"
            />
          </div>
        )}

        {/* Title - Always centered, will wrap under logo if needed */}
        <h1
          ref={titleRef}
          className={`text-xl font-bold text-gray-800 text-center whitespace-pre-line break-words hyphens-auto
            ${
              shouldWrap
                ? "mt-2"
                : "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full"
            }`}
        >
          {titleText}
        </h1>
      </div>
    </div>
  );
};

/**
 * Banner Component
 * Displays form banner image, handling both base64 and URL sources
 */
const BannerComponent = ({ banner_base64, banner_url, language }) => {
  const bannerSrc = banner_url?.[language] || banner_base64?.[language];
  const [aspectRatio, setAspectRatio] = React.useState(16 / 9); // default aspect ratio

  React.useEffect(() => {
    if (bannerSrc) {
      const img = new Image();
      img.onload = () => {
        setAspectRatio(img.naturalWidth / img.naturalHeight);
      };
      img.src = bannerSrc;
    }
  }, [bannerSrc]);

  if (!bannerSrc) return null;

  return (
    <div id="form-banner" className="rounded-lg overflow-hidden mb-2">
      <div
        style={{ paddingBottom: `${(1 / aspectRatio) * 100}%` }}
        className="relative w-full"
      >
        <img
          src={bannerSrc}
          alt="Form Banner"
          className="absolute inset-0 w-full h-full object-contain"
        />
      </div>
    </div>
  );
};

/**
 * Description Component
 * Renders form description with HTML support
 */
const DescriptionContainer = ({
  description,
  language,
  onLanguageChange,
  supportedLanguages,
  fontSize,
  onFontSizeChange,
  hasDetails,
  isFontSizeGuideOpen,
  setIsFontSizeGuideOpen,
}) => {
  const hasDescription = description?.[language];

  return (
    <div
      className={`
      mx-auto
      ${hasDetails ? "w-full max-w-6xl lg:max-w-4xl xl:max-w-6xl" : "max-w-2xl"}
    `}
    >
      <div
        className={`
        bg-green-0
        flex
        flex-col
        gap-2
        w-full
        p-4
        mb-2
        rounded-lg
      `}
      >
        {/* Buttons row - now includes font size guide button */}
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => setIsFontSizeGuideOpen(true)}
            className="px-3 py-1.5 text-blue-600 bg-green-0 rounded text-sm hover:bg-green-100"
          >
            {t("fontsize", language)}
          </button>
          <LangSwitch
            currentLang={language}
            onLanguageChange={onLanguageChange}
            supportedLanguages={supportedLanguages}
          />
        </div>

        {/* Description content - only shown if exists */}
        {hasDescription && (
          <div className="w-full [&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-800">
            <div dangerouslySetInnerHTML={{ __html: description[language] }} />
          </div>
        )}

        {/* Font Size Guide Modal */}
        <FontSizeGuide
          isOpen={isFontSizeGuideOpen}
          onClose={() => setIsFontSizeGuideOpen(false)}
          language={language}
        />
      </div>
    </div>
  );
};

/**
 * Availability Message Component
 * Shows form availability status based on valid_from and valid_to dates
 */
const AvailabilityMessageComponent = ({ formMetadata, language }) => {
  const now = new Date();
  const startDate = formMetadata.valid_from?.time
    ? parseHKTDatetimeStr(formMetadata.valid_from?.time)
    : null;
  const endDate = formMetadata.valid_to?.time
    ? parseHKTDatetimeStr(formMetadata.valid_to?.time)
    : null;

  const contactMessage = formMetadata.contact_email
    ? t("form.forInquiry", language, {
        contact_email: formMetadata.contact_email,
      })
    : "";

  // Not started yet
  if (startDate && now < startDate) {
    const availabilityMessage =
      formMetadata.valid_from?.msg?.[language] ||
      t("form.availability.notStarted", language, {
        datetime: formatDateTime(startDate, language).auto,
      });
    const message = `${availabilityMessage} ${contactMessage}`;
    return (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-2 p-2"
      >
        <p className="">{message}</p>
      </motion.div>
    );
  }

  // Expired
  else if (endDate && now > endDate) {
    const availabilityMessage =
      formMetadata.valid_to?.msg?.[language] ||
      t("form.availability.expired", language, {
        datetime: formatDateTime(endDate, language).date,
      });
    const message = `${availabilityMessage} ${contactMessage}`;
    return (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-2 p-2"
      >
        <p className="">{message}</p>
      </motion.div>
    );
  }

  // Generic unavailable message
  else {
    const availabilityMessage = t("form.availability.unavailable", language);
    const message = `${availabilityMessage} ${contactMessage}`;
    return (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-2 p-2"
      >
        <p className="">{message}</p>
      </motion.div>
    );
  }
};

/**
 * Details Component
 * Renders additional form details in a sidebar
 */
const DetailsComponent = ({ details, language }) => {
  if (!details?.[language]) return null;
  return (
    <div id="form-details" className="lg:col-span-1">
      <div
        className="border border-gray-300 rounded-lg p-4 text-gray-600
                   [&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-800"
        dangerouslySetInnerHTML={{ __html: details[language] }}
      />
    </div>
  );
};

const renderQuestionTitle = (question, language) => {
  if (question.type === "label") {
    // Get alignment class based on title_align property
    const alignmentClass = {
      left: "text-left",
      center: "text-center",
      right: "text-right",
    }[question.title_align || "left"];

    if (question.enable_rtf_title) {
      return (
        <div
          className={`font-medium text-gray-700 ${alignmentClass}`}
          dangerouslySetInnerHTML={{
            __html: question.rtf_title[language] || "",
          }}
        />
      );
    } else {
      return (
        <div className={`font-medium text-gray-700 ${alignmentClass}`}>
          {question.title[language]}
        </div>
      );
    }
  }

  return (
    <label
      className={`font-medium text-gray-700 ${
        question.always_display_answer_new_line ? "w-full" : "sm:w-1/3"
      }`}
    >
      {question.title[language]}
      {question.required && <span className="text-red-500 ml-1">*</span>}
    </label>
  );
};

// Individual Item Error Renderer (for list_text items)
const ItemErrorRenderer = ({ errors, fieldName, index }) => {
  const itemErrors = errors[`${fieldName}_${index}`];

  if (!itemErrors?.length) return null;

  return (
    <div className="text-red-500 text-sm pl-2">
      <ul className="list-disc list-inside">
        {itemErrors.map((err, i) => (
          <li key={i}>{err}</li>
        ))}
      </ul>
    </div>
  );
};

const SubformSection = ({
  question,
  formData,
  onChange,
  isDisabled,
  language,
  validationErrors,
  clearErrors,
}) => {
  return (
    <div className="border border-gray-200 rounded-lg bg-gray-50 p-4 space-y-4">
      {question.questions.map((subQuestion) => (
        <div key={subQuestion.object_id}>
          <div className="p-4 space-y-6">
            <QuestionInput
              question={subQuestion}
              formData={formData[question.field_name][0]}
              onChange={(field, value) => {
                const newSubformData = {
                  ...formData[question.field_name][0],
                  [field]: value,
                };
                onChange(question.field_name, [newSubformData]);
              }}
              isDisabled={isDisabled}
              language={language}
              validationErrors={validationErrors}
              clearErrors={clearErrors}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Question Input Component
 * Renders appropriate input element based on question type
 *
 * @param {Object} question - Question configuration
 * @param {Object} formData - Complete form state
 * @param {Function} onChange - Input change handler
 * @param {boolean} isDisabled - Whether input is disabled
 * @param {string} language - Current selected language
 * @param {string|string[]} error - Validation error(s) if any
 */
const QuestionInput = ({
  question,
  formData,
  onChange,
  isDisabled,
  language,
  validationErrors,
  clearErrors,
  questions,
}) => {
  const isDominated = React.useMemo(() => {
    if (!question.mutex_id) return false;

    const dominating = getDominatingOption(
      questions,
      formData,
      question.mutex_id
    );
    if (!dominating) return false;

    const dominatingIndex = questions.indexOf(dominating.question);
    const currentIndex = questions.indexOf(question);

    return currentIndex > dominatingIndex;
  }, [question, questions, formData]);

  // Update the input value when dominated
  React.useEffect(() => {
    if (isDominated && question.mutex_id) {
      const dominating = getDominatingOption(
        questions,
        formData,
        question.mutex_id
      );
      if (dominating) {
        onChange(question.field_name, dominating.value);
      }
    }
  }, [isDominated, question.mutex_id]);

  const effectivelyDisabled = isDisabled || isDominated;

  // Check both field-specific errors and mutex errors
  const getErrors = () => {
    const fieldErrors = validationErrors[question.field_name] || [];
    const mutexErrors = question.mutex_id
      ? validationErrors[question.mutex_id] || []
      : [];
    return [...fieldErrors, ...mutexErrors];
  };

  const errors = getErrors();
  const hasError = errors.length > 0;

  // Update baseInputStyles to use hasError
  const baseInputStyles = `w-full px-3 py-2 rounded border
  ${hasError ? "border-red-500" : "border-gray-300"}
  ${
    effectivelyDisabled
      ? "bg-gray-100 text-gray-500 cursor-not-allowed pointer-events-none select-none"
      : "bg-white text-gray-800"
  }
  placeholder-gray-400
  focus:border-blue-500 focus:ring-1 focus:ring-blue-500
  outline-none`;

  const renderError = () => {
    if (!hasError) return null;
    return (
      <div className="text-red-500 text-sm mt-1">
        <ul className="list-disc list-inside">
          {errors.map((err, index) => (
            <li key={index}>{err}</li>
          ))}
        </ul>
      </div>
    );
  };

  const inputElement = (() => {
    switch (question.type) {
      case "list_text": {
        const listCount = formData[question.field_name]?.length || 1;
        const maxCount = question.max_count;

        // Get count notice text with fallback
        const getCountNotice = () => {
          // If count_notice is available in form metadata, use it
          if (question.count_notice?.[language]) {
            return question.count_notice[language].replace(
              "{max_count}",
              maxCount.toString()
            );
          }
          // Fallback to locale.js
          return t("validation.items.count", language, {
            current: listCount,
            max_count: maxCount,
          });
        };

        const handleAddItem = (insertIndex) => {
          if (listCount < maxCount) {
            // Create new array with empty string inserted at the specified position
            const newArray = [...(formData[question.field_name] || [""])];
            newArray.splice(insertIndex + 1, 0, "");
            onChange(question.field_name, newArray);

            // Shift all subsequent items down
            for (let i = listCount - 1; i > insertIndex; i--) {
              const prevValue = formData[`${question.field_name}_${i}`] || "";
              onChange(`${question.field_name}_${i + 1}`, prevValue);
            }
            // Set the new inserted item to empty
            onChange(`${question.field_name}_${insertIndex + 1}`, "");
          }
        };

        const handleRemoveItem = (index) => {
          // Get current array
          const currentArray = [...(formData[question.field_name] || [""])];

          // Remove item at index
          currentArray.splice(index, 1);

          // Update main array
          onChange(question.field_name, currentArray);

          // Shift all subsequent items up
          for (let i = 0; i < maxCount - 1; i++) {
            onChange(`${question.field_name}_${i}`, currentArray[i] ?? "");
          }
        };

        const handleItemChange = (index, value) => {
          const maxLength = question.validation?.max_length;
          if (maxLength && value.length > maxLength) return;

          // Update individual field with underscore
          onChange(`${question.field_name}_${index}`, value);

          // Update array
          const newArray = [...(formData[question.field_name] || [])];
          newArray[index] = value;
          onChange(question.field_name, newArray);
        };

        return (
          <div className="space-y-2">
            {Array.from({ length: listCount }).map((_, index) => {
              const itemValue =
                formData[`${question.field_name}_${index}`] || "";
              const itemErrors =
                validationErrors[`${question.field_name}_${index}`];
              const hasError = !!itemErrors?.length;

              return (
                <div
                  key={`${question.field_name}_${index}`}
                  className="space-y-1"
                >
                  <div className="flex items-center gap-2">
                    <input
                      id={`${question.field_name}_${index}`}
                      type="text"
                      value={itemValue}
                      onChange={(e) => {
                        handleItemChange(index, e.target.value);
                      }}
                      disabled={isDisabled}
                      placeholder={question.description[language]}
                      className={`${baseInputStyles} flex-1 ${
                        hasError ? "border-red-500 focus:border-red-500" : ""
                      }`}
                    />
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => {
                          handleAddItem(index);
                          clearErrors();
                        }}
                        disabled={isDisabled || listCount >= maxCount}
                        className={`p-2 rounded transition-colors duration-200
                    ${
                      isDisabled || listCount >= maxCount
                        ? "text-gray-300 bg-gray-50 cursor-not-allowed"
                        : "text-blue-500 hover:text-white hover:bg-blue-500"
                    }`}
                      >
                        <Plus size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          handleRemoveItem(index);
                          clearErrors();
                        }}
                        disabled={isDisabled || listCount === 1}
                        className={`p-2 rounded transition-colors duration-200
                    ${
                      isDisabled || listCount === 1
                        ? "text-gray-300 bg-gray-50 cursor-not-allowed"
                        : "text-red-500 hover:text-white hover:bg-red-500"
                    }`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  {itemErrors && itemErrors.length > 0 && (
                    <ItemErrorRenderer
                      errors={validationErrors}
                      fieldName={question.field_name}
                      index={index}
                    />
                  )}
                </div>
              );
            })}

            {/* Count notice */}
            <div className="flex justify-end text-xs text-gray-500">
              {getCountNotice()}
            </div>
          </div>
        );
      }

      case "text":
        return (
          <input
            id={question.field_name}
            type={question.type}
            value={formData[question.field_name] || ""}
            onChange={(e) => {
              const newValue = e.target.value;
              const maxLength = question.validation?.max_length;
              // Only update if within length limits
              if (maxLength === null || newValue.length <= maxLength) {
                onChange(question.field_name, newValue);
              }
            }}
            disabled={isDisabled}
            placeholder={question.description[language]}
            className={baseInputStyles}
          />
        );

      case "textarea":
        const textareaMaxLength = question.validation?.max_length;
        const textareaCurrentLength = (formData[question.field_name] || "")
          .length;
        const currentLineCount =
          ((formData[question.field_name] || "").match(/\n/g) || []).length + 1;
        const maxRows = question.validation?.max_rows;

        return (
          <div className="w-full">
            <textarea
              id={question.field_name}
              rows={question.default_rows || 3}
              value={formData[question.field_name] || ""}
              onChange={(e) => {
                // Count line breaks to validate rows
                const lineCount =
                  (e.target.value.match(/\n/g) || []).length + 1;
                const maxRows = question.validation?.max_rows;

                const newValue = e.target.value;
                const maxLength = question.validation?.max_length;

                if (
                  (maxRows === null || lineCount <= maxRows) &&
                  (maxLength === null || newValue.length <= maxLength)
                ) {
                  onChange(question.field_name, e.target.value);
                }
              }}
              disabled={isDisabled}
              placeholder={question.description[language]}
              className={`${baseInputStyles} ${
                question.resizable ? "" : "resize-none"
              }`}
            />
            <div
              className={`flex text-xs text-gray-500 mt-1 ${
                maxRows ? "justify-between" : "justify-end"
              }`}
            >
              {maxRows && (
                <div>
                  {currentLineCount}/{maxRows}{" "}
                  {t("validation.rows.count", language)}
                </div>
              )}
              {textareaMaxLength && (
                <div>
                  {textareaCurrentLength}/{textareaMaxLength}{" "}
                  {t("validation.length.count", language)}
                </div>
              )}
            </div>
          </div>
        );

      case "time":
        return (
          <input
            id={question.field_name}
            type="time"
            value={formData[question.field_name] || ""}
            onChange={(e) => onChange(question.field_name, e.target.value)}
            disabled={isDisabled}
            className={baseInputStyles}
          />
        );

      case "date":
        return (
          <input
            id={question.field_name}
            type="date"
            value={formData[question.field_name] || ""}
            onChange={(e) => onChange(question.field_name, e.target.value)}
            disabled={isDisabled}
            className={baseInputStyles}
          />
        );

      case "dropdown":
        return (
          <select
            id={question.field_name}
            value={
              formData[question.field_name] === null
                ? ""
                : formData[question.field_name]
            }
            onChange={(e) => {
              const value =
                e.target.value === "" ? "" : parseInt(e.target.value);
              onChange(question.field_name, value);
            }}
            disabled={effectivelyDisabled}
            className={baseInputStyles}
          >
            <option value="">{t("form.pleaseSelect", language)}</option>
            {question.options?.map((option, index) => (
              <option key={option.object_id} value={index}>
                {option.desc[language]}
              </option>
            ))}
          </select>
        );

      case "radio":
        return (
          <div
            className={`flex-1 ${
              question.options_display_inline
                ? "flex flex-wrap gap-4"
                : "space-y-2"
            }`}
          >
            {question.options?.map((option, index) => (
              <label
                key={option.object_id}
                className="flex items-center gap-2 w-fit"
              >
                <input
                  id={question.field_name}
                  type="radio"
                  name={question.field_name}
                  value={index}
                  checked={formData[question.field_name] === index} // Compare with string index
                  onChange={(e) =>
                    onChange(question.field_name, parseInt(e.target.value))
                  }
                  disabled={effectivelyDisabled}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <span className="text-gray-700">{option.desc[language]}</span>
              </label>
            ))}
          </div>
        );

      case "checkboxgroup":
        return (
          <div
            className={`flex-1 ${
              question.options_display_inline
                ? "flex flex-wrap gap-4"
                : "space-y-2"
            }`}
          >
            {question.options?.map((option, index) => (
              <label
                key={option.object_id}
                className="flex items-start gap-2 w-fit"
              >
                <div className="flex-shrink-0 mt-1">
                  <input
                    id={`${question.field_name}_${index}`}
                    type="checkbox"
                    checked={
                      formData[`${question.field_name}_${index}`] ?? false
                    }
                    onChange={(e) => {
                      const isChecked = e.target.checked;

                      // Update individual checkbox state
                      onChange(`${question.field_name}_${index}`, isChecked);

                      // Clear other text input if unchecking
                      if (!isChecked && option.other_option) {
                        onChange(`${question.field_name}_${index}_other`, "");
                      }

                      // Update array of selected indices
                      const currentArray = Array.isArray(
                        formData[question.field_name]
                      )
                        ? [...formData[question.field_name]]
                        : [];

                      if (isChecked) {
                        onChange(
                          question.field_name,
                          [...currentArray, index].sort((a, b) => a - b)
                        );
                      } else {
                        onChange(
                          question.field_name,
                          currentArray
                            .filter((val) => val !== index)
                            .sort((a, b) => a - b)
                        );
                      }
                    }}
                    disabled={isDisabled}
                    className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                </div>
                <span className="text-gray-700">{option.desc[language]}</span>

                {option.other_option && (
                  <input
                    type="text"
                    value={
                      formData[`${question.field_name}_${index}_other`] || ""
                    }
                    onChange={(e) => {
                      const newValue = e.target.value;
                      const maxLength = question.validation?.other_max_length;

                      // Check max length before proceeding
                      if (maxLength && newValue.length > maxLength) return;

                      // Update other field value
                      onChange(
                        `${question.field_name}_${index}_other`,
                        newValue
                      );
                    }}
                    disabled={
                      isDisabled || !formData[`${question.field_name}_${index}`]
                    }
                    placeholder={
                      option.other_option_placeholder?.[language] ||
                      t("form.pleaseSpecify", language)
                    }
                    className={`ml-2 w-40 px-2 py-1 text-sm rounded border
        ${hasError ? "border-red-500" : "border-gray-300"}
        ${
          isDisabled || !formData[`${question.field_name}_${index}`]
            ? "bg-gray-100 text-gray-500"
            : "bg-white text-gray-800"
        }
        placeholder-gray-400
        focus:border-blue-500 focus:ring-1 focus:ring-blue-500
        outline-none`}
                  />
                )}
              </label>
            ))}
          </div>
        );

      case "checkbox":
        return (
          <div className="flex-1">
            <label className="flex items-center gap-2 w-fit">
              <input
                id={question.field_name}
                type="checkbox"
                checked={formData[question.field_name] ?? false}
                onChange={(e) =>
                  onChange(question.field_name, e.target.checked)
                }
                disabled={isDisabled}
                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-gray-700">
                {question.description[language]}
              </span>
            </label>
          </div>
        );

      case "phone_number": {
        const with_areacode = question.with_areacode ?? false;
        //const pattern = validation?.regex?.pattern;

        return (
          <div className="flex items-center gap-2">
            {with_areacode && (
              <>
                <input
                  type="text"
                  value={
                    splitAreaCodeAndNumber(formData[question.field_name])[0]
                  }
                  onChange={(e) => {
                    const currentPhoneNumber = splitAreaCodeAndNumber(
                      formData[question.field_name]
                    )[1];
                    const hasAreaCode = e.target.value.trim() !== "";

                    const newValue = hasAreaCode
                      ? `${e.target.value}-${currentPhoneNumber}`
                      : currentPhoneNumber;

                    onChange(question.field_name, newValue);
                  }}
                  disabled={effectivelyDisabled}
                  placeholder={t("form.phone.areaCode", language)}
                  className={`${baseInputStyles} w-1/3`}
                />
                <span className="text-gray-500">-</span>
              </>
            )}
            <input
              type="text"
              value={splitAreaCodeAndNumber(formData[question.field_name])[1]}
              onChange={(e) => {
                if (with_areacode) {
                  const currentAreaCode = splitAreaCodeAndNumber(
                    formData[question.field_name]
                  )[0];
                  const hasAreaCode = currentAreaCode.trim() !== "";
                  onChange(
                    question.field_name,
                    hasAreaCode
                      ? `${currentAreaCode}-${e.target.value}`
                      : e.target.value
                  );
                } else {
                  onChange(question.field_name, e.target.value);
                }
              }}
              disabled={effectivelyDisabled}
              placeholder={
                question.description[language] ||
                t("form.phone.phoneNumber", language)
              }
              className={`${baseInputStyles} ${
                with_areacode ? "w-2/3" : "w-full"
              }`}
            />
          </div>
        );
      }

      default:
        return null;
    }
  })();

  if (question.type === "list_subform") {
    const listCount = formData[question.field_name]?.length || 1;
    const maxCount = question.max_count;

    // Get count notice text with fallback
    const getCountNotice = () => {
      if (question.count_notice?.[language]) {
        return question.count_notice[language].replace(
          "{max_count}",
          maxCount.toString()
        );
      }
      return t("validation.items.count", language, {
        current: listCount,
        max_count: maxCount,
      });
    };

    const handleAddItem = (insertIndex) => {
      if (listCount < maxCount) {
        // Create empty subform data
        const emptySubformData = {};
        question.questions.forEach((subQ) => {
          emptySubformData[subQ.field_name] = "";
        });

        // Create new array with empty subform inserted at the specified position
        const newArray = [
          ...(formData[question.field_name] || [emptySubformData]),
        ];
        newArray.splice(insertIndex + 1, 0, emptySubformData);
        onChange(question.field_name, newArray);
      }
    };

    const handleRemoveItem = (index) => {
      // Get current array
      const currentArray = [...(formData[question.field_name] || [])];

      // Remove item at index
      currentArray.splice(index, 1);

      // Update main array
      onChange(question.field_name, currentArray);

      // Shift all subsequent items up
      for (let i = 0; i < maxCount - 1; i++) {
        onChange(`${question.field_name}_${i}`, currentArray[i] ?? {});
      }
    };

    return (
      <div className="space-y-4">
        <label
          className={`font-medium text-gray-700 ${
            question.always_display_answer_new_line
              ? "block w-full"
              : "sm:w-1/3"
          }`}
        >
          {question.title[language]}
          {question.required && <span className="text-red-500 ml-1">*</span>}
        </label>

        {Array.from({ length: listCount }).map((_, index) => (
          <div
            key={`${question.field_name}_${index}`}
            className="p-4 space-y-2 border border-gray-200 rounded-lg bg-gray-50"
          >
            <div className="flex justify-between items-center mb-2">
              <div className="font-medium text-gray-700">#{index + 1}</div>

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => {
                    handleAddItem(index);
                    clearErrors();
                  }}
                  disabled={isDisabled || listCount >= maxCount}
                  className={`p-2 rounded transition-colors duration-200
                  ${
                    isDisabled || listCount >= maxCount
                      ? "text-gray-300 bg-gray-50 cursor-not-allowed"
                      : "text-blue-500 hover:text-white hover:bg-blue-500"
                  }`}
                >
                  <Plus size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleRemoveItem(index);
                    clearErrors();
                  }}
                  disabled={isDisabled || listCount === 1}
                  className={`p-2 rounded transition-colors duration-200
                  ${
                    isDisabled || listCount === 1
                      ? "text-gray-300 bg-gray-50 cursor-not-allowed"
                      : "text-red-500 hover:text-white hover:bg-red-500"
                  }`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            {question.questions?.map((subQuestion) => (
              <div key={subQuestion.object_id}>
                <QuestionInput
                  question={subQuestion} // Use subQuestion's own always_display_answer_new_line setting
                  formData={formData[question.field_name][index]}
                  onChange={(field, value) => {
                    const newSubformData = {
                      ...formData[question.field_name][index],
                      [field]: value,
                    };
                    const newArray = [...formData[question.field_name]];
                    newArray[index] = newSubformData;
                    onChange(question.field_name, newArray);
                  }}
                  isDisabled={isDisabled}
                  language={language}
                  validationErrors={{
                    [subQuestion.field_name]:
                      validationErrors[
                        `${question.field_name}_${index}.${subQuestion.field_name}`
                      ] || [],
                  }}
                  clearErrors={clearErrors}
                />
              </div>
            ))}
          </div>
        ))}

        {/* Count notice */}
        <div className="flex justify-end text-xs text-gray-500">
          {getCountNotice()}
        </div>
      </div>
    );
  }

  // For label type questions, just render the title and return
  if (question.type === "label") {
    return renderQuestionTitle(question, language);
  }

  return (
    <div
      className={`flex flex-col gap-2 ${
        question.always_display_answer_new_line
          ? ""
          : "sm:flex-row sm:items-start sm:gap-4"
      }`}
    >
      {renderQuestionTitle(question, language)}

      {/* Rest of the input rendering logic */}
      <div
        className={`${
          question.always_display_answer_new_line ? "w-full" : "w-full sm:w-2/3"
        }`}
      >
        <div className="flex-1">
          {inputElement}
          {renderError()}
        </div>
      </div>
    </div>
  );
};

/**
 * Questions Component
 * Renders form questions with their inputs and validation states
 *
 * @param {Object[]} questions - Array of question configurations from formMetadata
 * @param {Object} formData - Current form values/answers
 * @param {Function} onInputChange - Handler for input changes
 * @param {boolean} isDisabled - Whether the inputs are disabled
 * @param {string} language - Current selected language
 * @param {Object} validationErrors - Validation errors by field name
 */
const QuestionsComponent = ({
  questions,
  formData,
  onInputChange,
  isDisabled,
  language,
  validationErrors,
  clearErrors,
}) => {
  return (
    <div id="form-questions">
      {/* Add mandatory field notice at the top */}
      <div className="flex justify-end mb-2 px-4">
        <div className="text-sm text-gray-600">
          <span className="text-red-500">*</span>{" "}
          {t("form.mandatoryField", language)}
        </div>
      </div>

      <div className="p-4 space-y-6">
        {questions.map((question) => (
          <motion.div
            key={question.object_id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <QuestionInput
              question={question}
              questions={questions}
              formData={formData}
              onChange={onInputChange}
              isDisabled={isDisabled}
              language={language}
              validationErrors={validationErrors}
              clearErrors={clearErrors}
            />
          </motion.div>
        ))}
      </div>
    </div>
  );
};

/**
 * Captcha Component
 * Handles captcha display, input, and refresh functionality
 */
const CaptchaComponent = ({
  value,
  onChange,
  onRefresh,
  isDisabled,
  captchaImage,
  language,
  isPreview,
}) => {
  return (
    <div id="captcha" className="mt-6 flex flex-col items-center gap-2">
      {/* CAPTCHA Image with Border */}
      <div className="w-48 h-12 flex items-center justify-center overflow-hidden border border-gray-300 rounded">
        {isPreview ? (
          // Preview mode: display image directly
          <img
            src={captchaImage}
            alt="CAPTCHA"
            className="w-full h-full object-contain"
          />
        ) : (
          // Normal mode: use base64 image
          captchaImage && (
            <img
              src={`data:image/png;base64,${captchaImage}`}
              alt="CAPTCHA"
              className="w-full h-full object-contain"
            />
          )
        )}
      </div>

      {/* CAPTCHA Input and Refresh Button */}
      <div className="w-48 flex items-center gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t("form.captcha.placeholder", language)}
          maxLength={4}
          className="w-48 flex-1 text-center px-2 py-2 bg-white rounded border border-gray-300 text-gray-800 placeholder-gray-400"
          disabled={isDisabled}
        />
        <button
          type="button"
          onClick={onRefresh}
          className="p-2 bg-gray-100 rounded border border-gray-300 text-gray-500 hover:text-gray-700 disabled:opacity-50"
          disabled={isDisabled || isPreview}
        >
          <RefreshCw size={16} />
        </button>
      </div>
    </div>
  );
};

const AcknowledgementPopupComponent = ({
  message,
  language,
  onClose,
  submissionId,
}) => {
  const displayMessage = `${message[language]}\n${t(
    "form.submission_ref_msg",
    language,
    { submission_id: submissionId }
  )}`;

  return (
    <motion.div
      id="ack-popup"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div className="bg-white rounded-lg w-full max-w-md p-6">
        <div className="text-center mb-6 text-gray-700 whitespace-pre-line">
          {displayMessage}
        </div>
        <button
          onClick={onClose}
          className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          {t("form.close", language)}
        </button>
      </div>
    </motion.div>
  );
};

const PICSComponent = ({
  pics,
  language,
  isAcknowledged,
  onAcknowledge,
  error,
  isDisabled,
}) => {
  const [isCheckboxEnabled, setIsCheckboxEnabled] = useState(false);
  const contentRef = useRef(null);
  if (!pics?.enabled || !pics?.content?.[language]) return null;

  const maxHeight =
    window.innerWidth <= 640
      ? pics.style?.mobileMaxHeight || "150px"
      : pics.style?.maxHeight || "200px";

  const handleScroll = (e) => {
    const element = e.target;
    const isAtBottom =
      Math.abs(
        element.scrollHeight - element.scrollTop - element.clientHeight
      ) < 1;

    if (isAtBottom && !isCheckboxEnabled) {
      setIsCheckboxEnabled(true);
    }
  };

  return (
    <div className="w-full mx-auto space-y-4">
      {" "}
      {/* Increased space-y from 2 to 4 */}
      {/* Scroll message with increased height for mobile */}
      <div
        className={`
          text-sm text-red-500 text-center
          min-h-[2rem] sm:h-6 /* Taller on mobile, normal on desktop */
          transition-opacity duration-300
          ${isCheckboxEnabled ? "opacity-0" : "opacity-100"}
        `}
      >
        {t("form.scrollToAccept", language)}
      </div>
      {/* Content container */}
      <div
        ref={contentRef}
        onScroll={handleScroll}
        className={`
          text-sm text-gray-700 border rounded-lg p-4 bg-white
          overflow-y-auto overscroll-contain
          scrollbar-none md:scrollbar
          ${error ? "border-red-500" : "border-gray-200"}
          -webkit-overflow-scrolling-touch
        `}
        style={{
          maxHeight,
          overscrollBehavior: "contain",
        }}
      >
        <div
          className={`
            touch-pan-y
            prose prose-sm max-w-none
            [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mb-4
            [&_h3]:text-base [&_h3]:font-bold [&_h3]:mt-6 [&_h3]:mb-4
            [&_p]:mb-4 [&_p:last-child]:mb-0
            [&_ol]:ml-4 [&_ol]:space-y-2 [&_ol]:mb-4
            [&_li]:pl-1
            [&_br]:hidden
            [&_.ql-indent-1]:ml-4
            [&_.ql-indent-2]:ml-12
            [&_.ql-indent-3]:ml-20
            [&_.hanging-indent-true.ql-indent-1]:pl-8
            [&_.hanging-indent-true.ql-indent-1]:-indent-8
            [&_.hanging-indent-true.ql-indent-2]:pl-8
            [&_.hanging-indent-true.ql-indent-2]:-indent-8
            [&_.hanging-indent-true.ql-indent-3]:pl-8
            [&_.hanging-indent-true.ql-indent-3]:-indent-8
          `}
          dangerouslySetInnerHTML={{ __html: pics.content[language] }}
        />
      </div>
      {/* Checkbox section with increased top margin */}
      <div className="flex flex-col items-center mt-4">
        {" "}
        {/* Added mt-4 */}
        <div className="w-full max-w-[700px]">
          <div className="flex items-start gap-2 justify-center">
            <span className="text-red-500 mt-1">*</span>
            <input
              type="checkbox"
              id="pics_acknowledged"
              checked={isAcknowledged}
              onChange={(e) => onAcknowledge(e.target.checked)}
              disabled={isDisabled || !isCheckboxEnabled}
              className={`mt-1 h-4 w-4 rounded border-gray-300 text-blue-600
                focus:ring-blue-500 ${error ? "border-red-500" : ""}`}
            />
            <label
              htmlFor="pics_acknowledged"
              className="text-sm text-gray-700"
            >
              {t("form.picsAcknowledgment", language)}
            </label>
          </div>

          {error && (
            <div className="text-sm text-red-500 mt-2 text-center">
              {t("validation.picsRequired", language)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const FormRenderer = ({
  formMetadata,
  showSubmitButton = true,
  mode,
  isPreview = false,
}) => {
  console.log(formMetadata);
  const [fontSize, setFontSize] = useState("1.125"); // Default size
  const [isFontSizeGuideOpen, setIsFontSizeGuideOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(
    formMetadata.supported_languages?.[0] || "en"
  );
  const [formData, setFormData] = useState(() =>
    initializeFormData(formMetadata.questions)
  );
  const [isFormAvailable, setIsFormAvailable] = useState(true);
  const [captchaImage, setCaptchaImage] = useState("");
  const [captchaId, setCaptchaId] = useState("");
  const [captchaValue, setCaptchaValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);
  const [showAcknowledgment, setShowAcknowledgment] = useState(false);

  const [isPicsAcknowledged, setIsPicsAcknowledged] = useState(false);
  const [picsError, setPicsError] = useState(false);

  const fetchCaptcha = useCallback(async () => {
    if (isPreview) {
      setCaptchaImage(`${PREFIX}/image/captcha.png`);
      setCaptchaId("preview-captcha");
      return;
    }
    try {
      const response = await fetch(CAPTCHA_URL);
      const data = await response.json();
      setCaptchaImage(data.image_base64);
      setCaptchaId(data.captcha_id);
      setCaptchaValue("");
    } catch (error) {
      console.error("Failed to fetch captcha:", error);
    }
  }, [isPreview]);

  // Add useEffect to apply font size
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--base-font-size",
      `${fontSize}rem`
    );
  }, [fontSize]);

  // Add useEffect to fetch captcha on mount
  useEffect(() => {
    fetchCaptcha();
  }, [fetchCaptcha]);

  // Move checkAvailability outside useEffect so it can be reused
  const checkAvailability = () => {
    const now = new Date();
    const startDate = formMetadata.valid_from?.time
      ? parseHKTDatetimeStr(formMetadata.valid_from?.time)
      : null;
    const endDate = formMetadata.valid_to?.time
      ? parseHKTDatetimeStr(formMetadata.valid_to?.time)
      : null;

    const isNotStarted = startDate && now < startDate;
    const isExpired = endDate && now > endDate;
    const isAvailable = !isNotStarted && !isExpired;

    setIsFormAvailable(isAvailable);
    return { isNotStarted, isExpired, isAvailable };
  };

  useEffect(() => {
    // Call checkAvailability initially
    checkAvailability();

    // Call checkAvailability every second
    const intervalId = setInterval(() => {
      checkAvailability();
    }, 1000);

    // Clear the interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  const handleLanguageChange = (newLanguage) => {
    setSelectedLanguage(newLanguage);
    clearErrors();
  };

  const handleInputChange = (fieldName, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const validate = (question, value, language) => {
    const errors = {};

    const addError = (field, error) => {
      if (!errors[field]) {
        errors[field] = [];
      }
      errors[field].push(error);
    };

    const hasValue = Boolean(
      // NOTE: for radio and dropdown, value can be 0
      // NOTE: for phone_number, it may be '-'. so will check require differently
      value !== undefined &&
        value !== null &&
        (Array.isArray(value)
          ? value.length > 0
          : typeof value === "string"
          ? value.trim() !== ""
          : value !== "")
    );

    // Required field validation
    if (question.required) {
      const requiredErrorMsg =
        question.mandatory_warning[language] ||
        t("validation.required", language);

      switch (question.type) {
        case "checkbox":
          if (!hasValue || value === false) {
            addError(question.field_name, requiredErrorMsg);
          }
          break;

        case "list_text": {
          if (!hasValue) {
            addError(`${question.field_name}_0`, requiredErrorMsg);
          }
          if (Array.isArray(value)) {
            // Validate each item in the list
            value.forEach((item, index) => {
              const itemErrors = [];

              // For first field when required
              if (index === 0 && question.required && !item.trim()) {
                addError(`${question.field_name}_${index}`, requiredErrorMsg);
              }
              // For additional fields (index > 0), they must have value if they exist
              else if (index > 0 && !item.trim()) {
                addError(`${question.field_name}_${index}`, requiredErrorMsg);
              }
            });
          }

          break;
        }

        case "phone_number": {
          const with_areacode = question.with_areacode ?? false;
          let areaCode = "";
          let phoneNumber = "";
          [areaCode, phoneNumber] = splitAreaCodeAndNumber(value);
          if (with_areacode) {
            if (
              areaCode === undefined ||
              areaCode === null ||
              areaCode.trim() === ""
            ) {
              addError(
                question.field_name,
                t("validation.areacode.required", language)
              );
            }
          }

          if (
            phoneNumber === undefined ||
            phoneNumber === null ||
            phoneNumber.trim() === ""
          ) {
            addError(
              question.field_name,
              t("validation.phone.required", language)
            );
          }

          break;
        }

        default:
          if (!hasValue) {
            addError(question.field_name, requiredErrorMsg);
          }
          break;
      }
    }

    // Skip other validations if empty value and not required
    const isEmptyAndOptional =
      (value === "" || value === null) && !question.required;
    if (isEmptyAndOptional) {
      return errors;
    }

    if (hasValue) {
      // Type-specific validations
      switch (question.type) {
        case "list_text": {
          if (!Array.isArray(value)) break;

          // Validate each item in the list
          value.forEach((item, index) => {
            const validation = question.validation ?? {};

            // Only validate non-empty items
            if (item.trim()) {
              // Length validation
              if (
                validation?.min_length !== null ||
                validation?.max_length !== null
              ) {
                if (
                  validation.min_length !== null &&
                  validation.max_length !== null
                ) {
                  if (validation.min_length === validation.max_length) {
                    if (item.length !== validation.min_length) {
                      addError(
                        `${question.field_name}_${index}`,
                        t("validation.length.exact", language, {
                          length: validation.min_length,
                        })
                      );
                    }
                  } else if (
                    item.length < validation.min_length ||
                    item.length > validation.max_length
                  ) {
                    addError(
                      `${question.field_name}_${index}`,
                      t("validation.length.between", language, {
                        min: validation.min_length,
                        max: validation.max_length,
                      })
                    );
                  }
                } else if (
                  validation.min_length !== null &&
                  item.length < validation.min_length
                ) {
                  addError(
                    `${question.field_name}_${index}`,
                    t("validation.length.min", language, {
                      min: validation.min_length,
                    })
                  );
                } else if (
                  validation.max_length !== null &&
                  item.length > validation.max_length
                ) {
                  addError(
                    `${question.field_name}_${index}`,
                    t("validation.length.max", language, {
                      max: validation.max_length,
                    })
                  );
                }
              }

              // Regex validation
              if (validation?.regex?.pattern) {
                const regex = new RegExp(validation.regex.pattern);
                if (!regex.test(item)) {
                  const regexMessage = t("validation.regex", language);
                  const exampleMessage = validation.regex.example
                    ? " " +
                      t("validation.example", language, {
                        example: validation.regex.example,
                      })
                    : "";
                  addError(
                    `${question.field_name}_${index}`,
                    regexMessage + exampleMessage
                  );
                }
              }
            }
          });
          break;
        }

        case "textarea": {
          if (value === "") break;

          const validation = question.validation ?? {};
          const lineCount = (value.match(/\n/g) || []).length + 1;

          // Length validation
          if (
            validation?.min_length !== null ||
            validation?.max_length !== null
          ) {
            if (
              validation.min_length !== null &&
              validation.max_length !== null
            ) {
              if (validation.min_length === validation.max_length) {
                if (value.length !== validation.min_length) {
                  addError(
                    question.field_name,
                    t("validation.length.exact", language, {
                      length: validation.min_length,
                    })
                  );
                }
              } else if (
                (value && value.length < validation.min_length) ||
                value.length > validation.max_length
              ) {
                addError(
                  question.field_name,
                  t("validation.length.between", language, {
                    min: validation.min_length,
                    max: validation.max_length,
                  })
                );
              }
            } else if (
              validation.min_length !== null &&
              value.length < validation.min_length
            ) {
              addError(
                question.field_name,
                t("validation.length.min", language, {
                  min: validation.min_length,
                })
              );
            } else if (
              validation.max_length !== null &&
              value.length > validation.max_length
            ) {
              addError(
                question.field_name,
                t("validation.length.max", language, {
                  max: validation.max_length,
                })
              );
            }
          }

          // Row count validation
          if (validation?.min_rows !== null || validation?.max_rows !== null) {
            if (validation.min_rows !== null && validation.max_rows !== null) {
              if (validation.min_rows === validation.max_rows) {
                if (lineCount !== validation.min_rows) {
                  addError(
                    question.field_name,
                    t("validation.rows.exact", language, {
                      count: validation.min_rows,
                    })
                  );
                }
              } else if (
                lineCount < validation.min_rows ||
                lineCount > validation.max_rows
              ) {
                addError(
                  question.field_name,
                  t("validation.rows.between", language, {
                    min: validation.min_rows,
                    max: validation.max_rows,
                  })
                );
              }
            } else if (
              validation.min_rows !== null &&
              lineCount < validation.min_rows
            ) {
              addError(
                question.field_name,
                t("validation.rows.min", language, {
                  min: validation.min_rows,
                })
              );
            }
          } else if (
            validation.max_rows !== null &&
            lineCount > validation.max_rows
          ) {
            addError(
              question.field_name,
              t("validation.rows.max", language, {
                min: validation.max_rows,
              })
            );
          }
          break;
        }

        case "text": {
          if (value === "") break;

          const validation = question.validation ?? {};

          // Length validation
          if (
            validation?.min_length !== null ||
            validation?.max_length !== null
          ) {
            if (
              validation.min_length !== null &&
              validation.max_length !== null
            ) {
              if (validation.min_length === validation.max_length) {
                if (value.length !== validation.min_length) {
                  addError(
                    question.field_name,
                    t("validation.length.exact", language, {
                      length: validation.min_length,
                    })
                  );
                }
              } else if (
                value &&
                (value.length < validation.min_length ||
                  value.length > validation.max_length)
              ) {
                addError(
                  question.field_name,
                  t("validation.length.between", language, {
                    min: validation.min_length,
                    max: validation.max_length,
                  })
                );
              }
            } else if (
              validation.min_length !== null &&
              value.length < validation.min_length
            ) {
              addError(
                question.field_name,
                t("validation.length.min", language, {
                  min: validation.min_length,
                })
              );
            } else if (
              validation.max_length !== null &&
              value.length > validation.max_length
            ) {
              addError(
                question.field_name,
                t("validation.length.max", language, {
                  max: validation.max_length,
                })
              );
            }
          }

          // Regex validation
          if (validation?.regex?.pattern) {
            const regex = new RegExp(validation.regex.pattern);
            if (!regex.test(value)) {
              const regexMessage = t("validation.regex", language);
              const exampleMessage = validation.regex.example
                ? " " +
                  t("validation.example", language, {
                    example: validation.regex.example,
                  })
                : "";
              addError(question.field_name, regexMessage + exampleMessage);
            }
          }
          break;
        }

        case "phone_number": {
          if (!value || value === "-") break;

          const validation = question.validation ?? {};
          const with_areacode = question.with_areacode ?? false;

          let areaCode = "";
          let phoneNumber = "";
          [areaCode, phoneNumber] = splitAreaCodeAndNumber(value);
          if (with_areacode) {
            if (areaCode && areaCode.length > AreaCodeMaxLength) {
              addError(
                question.field_name,
                t("validation.areacode.max", language, { max: 8 })
              );
            }
          }

          if (
            validation?.min_length !== null &&
            (phoneNumber?.length ?? 0) < validation.min_length
          ) {
            addError(
              question.field_name,
              t("validation.phone.min", language, {
                min: validation.min_length,
              })
            );
          }
          if (
            validation?.max_length !== null &&
            (phoneNumber?.length ?? 0) > validation.max_length
          ) {
            addError(
              question.field_name,
              t("validation.phone.max", language, {
                max: validation.max_length,
              })
            );
          }

          // Regex validation if pattern exists
          if (validation?.regex?.pattern) {
            const regex = new RegExp(validation.regex.pattern);
            if (!regex.test(value)) {
              const regexMessage = t("validation.regex", language);
              const exampleMessage = validation.regex.example
                ? " " +
                  t("validation.example", language, {
                    example: validation.regex.example,
                  })
                : "";
              addError(question.field_name, regexMessage + exampleMessage);
            }
          }

          break;
        }

        case "checkboxgroup": {
          if (!Array.isArray(value)) break;

          const validation = question.validation;
          if (
            validation?.min_selections !== null ||
            validation?.max_selections !== null
          ) {
            if (
              validation.min_selections !== null &&
              validation.max_selections !== null
            ) {
              if (validation.min_selections === validation.max_selections) {
                if (value.length !== validation.min_selections) {
                  addError(
                    question.field_name,
                    t("validation.selections.exact", language, {
                      count: validation.min_selections,
                    })
                  );
                }
              } else if (
                value.length < validation.min_selections ||
                value.length > validation.max_selections
              ) {
                addError(
                  question.field_name,
                  t("validation.selections.between", language, {
                    min: validation.min_selections,
                    max: validation.max_selections,
                  })
                );
              }
            } else if (
              validation.min_selections !== null &&
              value.length < validation.min_selections
            ) {
              addError(
                question.field_name,
                t("validation.selections.min", language, {
                  min: validation.min_selections,
                })
              );
            } else if (
              validation.max_selections !== null &&
              value.length > validation.max_selections
            ) {
              addError(
                question.field_name,
                t("validation.selections.max", language, {
                  max: validation.max_selections,
                })
              );
            }
          }

          // Validate "other" fields
          question.options?.forEach((option, index) => {
            if (
              option.other_option &&
              formData[`${question.field_name}_${index}`]
            ) {
              const otherValue =
                formData[`${question.field_name}_${index}_other`];

              if (!otherValue || !otherValue.trim()) {
                addError(
                  `${question.field_name}`,
                  t("validation.otherRequired", language)
                );
              } else if (
                validation?.other_max_length &&
                otherValue.length > validation.other_max_length
              ) {
                addError(
                  `${question.field_name}`,
                  t("validation.otherMaxLength", language, {
                    max: validation.other_max_length,
                  })
                );
              }
            }
          });

          break;
        }

        case "radio":
        case "dropdown":
        case "checkbox":
        case "date":
        case "time":
          break;

        default:
          break;
      }
    }
    return errors;
  };

  const validateSubform = (question, subformData, index, language) => {
    const errors = {};
    const addError = (field, error) => {
      if (!errors[field]) {
        errors[field] = [];
      }
      errors[field].push(error);
    };

    // Required validation for subform as a whole
    if (
      question.required &&
      (!subformData || Object.keys(subformData).length === 0)
    ) {
      addError(
        `${question.field_name}`,
        question.mandatory_warning[language] ||
          t("validation.required", language)
      );
      return errors;
    }

    // Validate each field in the subform
    question.questions.forEach((subQuestion) => {
      const subValue = subformData[subQuestion.field_name];
      const subErrors = validate(subQuestion, subValue, language);

      // Add errors with indexed field names
      Object.entries(subErrors).forEach(([field, errorMessages]) => {
        const indexedField = `${question.field_name}_${index}.${field}`;
        errors[indexedField] = errorMessages;
      });
    });

    return errors;
  };

  const validateForm = () => {
    let fieldErrors = {};

    const addError = (field, error) => {
      if (!fieldErrors[field]) {
        fieldErrors[field] = [];
      }
      fieldErrors[field].push(error);
    };

    formMetadata.questions.forEach((question) => {
      const value = formData[question.field_name];
      if (question.type == "list_subform") {
        // Validate each subform item
        if (Array.isArray(value)) {
          value.forEach((subformData, index) => {
            const subformErrors = validateSubform(
              question,
              subformData,
              index,
              selectedLanguage
            );
            Object.assign(fieldErrors, subformErrors);
          });
        }
      } else {
        const errors = validate(question, value, selectedLanguage);
        Object.entries(errors).forEach(([field, errorMessages]) => {
          fieldErrors[field] = errorMessages;
        });
      }
    });

    const mutexSelections = {};

    formMetadata.questions.forEach((question) => {
      // Skip if question has no mutex_id
      if (!question.mutex_id) return;

      const value = formData[question.field_name];

      // Skip if no value (allowing 0 as valid value)
      if (value === undefined || value === null || value === "") return;

      // Get the selected option
      const selectedOption = question.options?.[value];

      // Skip if the selected option has exclude_mutex=true
      if (selectedOption?.exclude_mutex) return;

      // Store the selection under this mutex_id
      if (!mutexSelections[question.mutex_id]) {
        mutexSelections[question.mutex_id] = [];
      }

      mutexSelections[question.mutex_id].push({
        value,
        field_name: question.field_name,
        desc: selectedOption?.desc?.[selectedLanguage] || value,
      });
    });

    // Check for mutex violations - if same value selected in different fields
    Object.entries(mutexSelections).forEach(([mutexId, selections]) => {
      const uniqueValues = new Set(selections.map((s) => s.value));
      const duplicateSelections = selections.filter(
        (s) => selections.filter((other) => other.value === s.value).length > 1
      );

      if (duplicateSelections.length > 0) {
        duplicateSelections.forEach((selection) => {
          const mutexMessage =
            t(`validation.mutex.conflict.${mutexId}`, selectedLanguage) ||
            t("validation.mutex.conflict._default", selectedLanguage);
          addError(selection.field_name, mutexMessage);
        });
      }
    });

    setValidationErrors(fieldErrors);
    return Object.keys(fieldErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitted) return;

    // clear all errors first
    clearErrors();

    // Check availability before submission
    checkAvailability();

    if (!isFormAvailable) {
      return;
    }

    // Check PICS acknowledgment first if enabled
    if (formMetadata.pics?.enabled && !isPicsAcknowledged) {
      setPicsError(true);
    }

    const isValid = validateForm();

    // Check CAPTCHA if required
    if (formMetadata.requireCaptcha && !captchaValue.trim()) {
      setSubmitError(t("errors.captchaRequired", selectedLanguage));
      return;
    }

    if (!isValid || (formMetadata.pics?.enabled && !isPicsAcknowledged)) {
      return;
    }

    setIsSubmitting(true);

    if (isPreview) {
      // Simulate successful submission in preview mode
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulate API delay
      setSubmitted(true);
      setIsSubmitting(false);
      return;
    }

    // Only include captcha_data in the request if CAPTCHA is required
    const requestBody = {
      mode: mode,
      form_id: formMetadata.form_id,
      selected_language: selectedLanguage,
      form_data: formData,
      ...(formMetadata.requireCaptcha && {
        captcha_data: { captcha_id: captchaId, captcha_ans: captchaValue },
      }),
    };

    try {
      const response = await fetch(SUBMISSION_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();

      // Handle different error codes
      if (result.success === "0") {
        switch (result.error_code) {
          case "2":
            setSubmitError(t("errors.captchaRequired", selectedLanguage));
            break;
          case "3":
            setSubmitError(t("errors.captchaExpired", selectedLanguage));
            await fetchCaptcha();
            break;
          case "4":
            setSubmitError(t("errors.captchaInvalid", selectedLanguage));
            await fetchCaptcha();
            break;
          case "6":
            setSubmitError(t("errors.formNotFound", selectedLanguage));
            break;
          case "7":
            setSubmitError(t("errors.formNotAvailable", selectedLanguage));
            const { isNotStarted, isExpired } = checkAvailability();
            if (isExpired) {
              setSubmitError(t("errors.formExpired", selectedLanguage));
            } else if (isNotStarted) {
              setSubmitError(t("errors.formNotStarted", selectedLanguage));
            } else {
              setSubmitError(t("errors.formNotAvailable", selectedLanguage));
            }
            break;
          case "99":
            setSubmitError(t("errors.invalidRequest", selectedLanguage));
            console.error("Invalid request:", result.message);
            break;
          case "999":
            setSubmitError(t("errors.unknownError", selectedLanguage));
            console.error("Unknown error:", result.message);
            break;
          default:
            setSubmitError(t("errors.submitFailed", selectedLanguage));
            console.error("Submission failed:", result.message);
        }
        return;
      }

      if (result.success === "1") {
        setSubmitted(true);
        setShowAcknowledgment(true);
        setSubmissionId(result.submission_id);
        console.log("Form submitted successfully:", result);
      } else {
        setSubmitError(result.message);
      }
    } catch (error) {
      setSubmitError(t("errors.submitFailed", selectedLanguage));
      console.error("Submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearErrors = () => {
    setValidationErrors({});
    setSubmitError("");
    setPicsError(false);
  };

  const handleClear = () => {
    clearErrors();
    setFormData(initializeFormData(formMetadata.questions));
    setIsPicsAcknowledged(false);
    setCaptchaValue("");
    if (formMetadata.requireCaptcha) {
      setCaptchaValue("");
      fetchCaptcha();
    }
  };

  const handleClose = () => {
    setShowAcknowledgment(false);
    // Optional: redirect after closing
    if (formMetadata.acknowledgmentRedirectUrl) {
      window.location.href = formMetadata.acknowledgmentRedirectUrl;
    }
  };

  // Determine if the form should be disabled
  const isDisabled = !isFormAvailable || isPreview || submitted || isSubmitting;

  const hasDescription = formMetadata.description[selectedLanguage];
  const hasDetails = formMetadata.details[selectedLanguage];

  const renderFormContent = () => {
    if (!isFormAvailable) {
      return (
        <div className="max-w-6xl mx-auto px-2 py-4">
          <LogoComponent
            logo_base64={formMetadata.logo_base64}
            logo_url={formMetadata.logo_url}
            title={formMetadata.title}
            language={selectedLanguage}
          />

          <BannerComponent
            banner_base64={formMetadata.banner_base64}
            banner_url={formMetadata.banner_url}
            language={selectedLanguage}
          />

          <div className="max-w-6xl mx-auto">
            {/* Show availability message in all supported languages */}
            <div>
              {formMetadata.supported_languages.map((lang) => (
                <AvailabilityMessageComponent
                  key={lang}
                  formMetadata={formMetadata}
                  language={lang}
                />
              ))}
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="max-w-6xl mx-auto px-2 py-4">
          <LogoComponent
            logo_base64={formMetadata.logo_base64}
            logo_url={formMetadata.logo_url}
            title={formMetadata.title}
            language={selectedLanguage}
          />

          <BannerComponent
            banner_base64={formMetadata.banner_base64}
            banner_url={formMetadata.banner_url}
            language={selectedLanguage}
          />

          <DescriptionContainer
            description={formMetadata.description}
            language={selectedLanguage}
            onLanguageChange={handleLanguageChange}
            supportedLanguages={formMetadata.supported_languages}
            fontSize={fontSize}
            onFontSizeChange={setFontSize}
            hasDetails={hasDetails}
            isFontSizeGuideOpen={isFontSizeGuideOpen}
            setIsFontSizeGuideOpen={setIsFontSizeGuideOpen}
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <DetailsComponent
              details={formMetadata.details}
              language={selectedLanguage}
            />

            <div
              className={
                formMetadata.details?.[selectedLanguage]
                  ? "lg:col-span-2"
                  : "lg:col-span-3 flex justify-center"
              }
            >
              <div
                className={`w-full ${
                  !formMetadata.details?.[selectedLanguage] ? "max-w-2xl" : ""
                }`}
              >
                <form onSubmit={handleSubmit} className="space-y-6">
                  <QuestionsComponent
                    questions={formMetadata.questions}
                    formData={formData}
                    onInputChange={handleInputChange}
                    isDisabled={isDisabled}
                    language={selectedLanguage}
                    validationErrors={validationErrors}
                    clearErrors={clearErrors}
                  />

                  {showSubmitButton && (
                    <>
                      {formMetadata.pics?.enabled && (
                        <PICSComponent
                          pics={formMetadata.pics}
                          language={selectedLanguage}
                          isAcknowledged={isPicsAcknowledged}
                          onAcknowledge={setIsPicsAcknowledged}
                          error={picsError}
                          isDisabled={isDisabled}
                        />
                      )}

                      {/* Only show CAPTCHA if required */}
                      {formMetadata.requireCaptcha && (
                        <CaptchaComponent
                          value={captchaValue}
                          onChange={setCaptchaValue}
                          onRefresh={fetchCaptcha}
                          isDisabled={isDisabled}
                          captchaImage={captchaImage}
                          language={selectedLanguage}
                          isPreview={isPreview}
                        />
                      )}

                      {submitError && (
                        <div className="text-red-500 text-sm mt-2 text-center">
                          {submitError}
                        </div>
                      )}

                      <div className="flex justify-center gap-8 mt-6">
                        {/* Clear Button */}
                        {/* Only show Clear Button if clearBtn is true */}
                        {formMetadata.clearBtn !== false && (
                          <motion.button
                            type="button"
                            onClick={handleClear}
                            disabled={isDisabled}
                            className="px-8 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400"
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.99 }}
                            style={{ minWidth: "150px" }}
                          >
                            {t("form.clear", selectedLanguage)}
                          </motion.button>
                        )}

                        {/* Submit Button */}
                        <motion.button
                          type="submit"
                          disabled={isDisabled}
                          className="px-8 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-400"
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.99 }}
                          style={{ minWidth: "150px" }}
                        >
                          {isSubmitting
                            ? t("form.submitting", selectedLanguage)
                            : t("form.submit", selectedLanguage)}
                        </motion.button>
                      </div>
                    </>
                  )}
                </form>

                {/* Acknowledgment message below submit button */}
                {submitted &&
                  formMetadata.acknowledgmentDisplay ===
                    "belowSubmitButton" && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-6 p-4 bg-green-50 border border-green-100 rounded-lg text-center text-gray-700"
                    >
                      {formMetadata.acknowledgment[selectedLanguage]}
                    </motion.div>
                  )}

                {/* Popup acknowledgment */}
                {submitted &&
                  showAcknowledgment &&
                  formMetadata.acknowledgmentDisplay === "popup" && (
                    <AcknowledgementPopupComponent
                      message={formMetadata.acknowledgment}
                      language={selectedLanguage}
                      onClose={handleClose}
                      submissionId={submissionId}
                    />
                  )}
              </div>
            </div>
          </div>

          {process.env.NODE_ENV === "development" && (
            <FormDataPanel
              formData={formData}
              validationErrors={validationErrors}
            />
          )}
        </div>
      );
    }
  };

  return renderFormContent();
};

export default FormRenderer;
